// Basic
import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
// Store & actions
import actionsOrganizations from '../../../api/actionsOrganizations';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterForm from '@/common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Common
import { useToast } from '@/common-utils/hooks/use-toast';
import ORGANIZATION_TYPES from '../../../enums/OrganizationTypes';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.urlSubDomain
 * @param root0.organizationId
 * @param root0.setLaunchRefetch
 * @param root0.organizationType
 * @param root0.modalTitle
 * @returns {JSX} ModalUpdateSubDomain
 */
function ModalUpdateSubDomain({ isOpen, setIsOpen, urlSubDomain, organizationId, setLaunchRefetch, organizationType, modalTitle }) {
    // Toast
    const { toast } = useToast();

    // State
    const [showSubmitButton, setShowSubmitButton] = useState(true);

    // Reset submit button when modal opens
    useEffect(() => {
        if (isOpen) {
            setShowSubmitButton(true);
        }
    }, [isOpen]);

    // Hooks  -- useMemo
    const { https, inputSubDomainName, domainSuffix, subDomain } = useMemo(() => {
        // Parse URL components and extract domain suffix dynamically
        const DEFAULT_URL = 'https://.smart-pos.eu/';
        const url = urlSubDomain || DEFAULT_URL;
        const [, protocol, inputSubDomain, domainName] = url.match(/^(https?:\/\/)([^/.]*)\.([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]\.[a-zA-Z0-9]+)\/$/);
        return {
            https: protocol,
            inputSubDomainName: inputSubDomain,
            domainSuffix: `.${domainName}`,
            subDomain: domainName
        };
    }, [urlSubDomain]);

    // Validation Schema
    const formSchema = z.object({
        subDomainName: z.string()
            .nonempty()
            .max(100)
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            subDomainName: organizationType === ORGANIZATION_TYPES.MERCHANT.name ? '' : inputSubDomainName
        }
    });

    // Api calls
    const onSuccess = () => {
        setShowSubmitButton(false);
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity
                entity={organizationType === ORGANIZATION_TYPES.MERCHANT.name ? 'modal.update_organization_type.success' : 'modal.update_subdomain.success'}
                namespace={nsOrganization}
            />,
            variant: 'success'
        });
    };
    const onError = (error) => {
        const errorCode = ['SUBDOMAIN_ALREADY_EXISTS', 'DOMAIN_ALREADY_USED'].includes(error.response.data.errorCode) ?
            'already_used' : 'default';
        const errorEntityPreffix = organizationType === ORGANIZATION_TYPES.MERCHANT.name ?
            'modal.update_organization_type.error.' : 'modal.update_subdomain.error.';
        setShowSubmitButton(false);
        setIsOpen(false);
        toast({
            description: <Entity entity={`${errorEntityPreffix}${errorCode}`} namespace={nsOrganization} />,
            variant: 'danger'
        });
    };
    const submitUpdateSubDomain = actionsOrganizations.updateSubDomain(onSuccess, onError);
    const submitUpdateOrganizationType = actionsOrganizations.updateOrganizationType(onSuccess, onError);

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className='max-w-lg'>
                <HeaderTitle title={modalTitle} namespace={nsOrganization} />
                <ModalBody>
                    <Form {...form}>
                        <FormField
                            name='subDomainName'
                            control={form.control}
                            render={({ field }) => (
                                <FormItem>
                                    <div className='flex items-center gap-2'>
                                        <span className='text-gray-500'>{https}</span>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                type='text'
                                                placeholder={subDomain}
                                                autoComplete='off'
                                            />
                                        </FormControl>
                                        <span className='text-gray-500 whitespace-nowrap'>{domainSuffix}</span>
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </Form>
                </ModalBody>
                <FooterForm
                    onSubmit={
                        () => {
                            const { subDomainName } = form.getValues();
                            if (organizationType === ORGANIZATION_TYPES.MERCHANT.name) {
                                submitUpdateOrganizationType.mutate({
                                    uuid: organizationId,
                                    subdomain: subDomainName
                                });
                            }
                            else {
                                submitUpdateSubDomain.mutate({
                                    uuid: organizationId,
                                    subdomainName: subDomainName
                                });
                            }
                        }
                    }
                    isSubmitting={submitUpdateSubDomain.isLoading || submitUpdateOrganizationType.isLoading}
                    showSubmitButton={showSubmitButton}
                    isSubmitDisabled={!form.formState.isValid}
                />
            </ModalContent>
        </Modal>
    );
}

ModalUpdateSubDomain.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    urlSubDomain: PropTypes.string,
    organizationId: PropTypes.string.isRequired,
    setLaunchRefetch: PropTypes.func,
    organizationType: PropTypes.string,
    modalTitle: PropTypes.string
};

export default ModalUpdateSubDomain;